import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiActivity, FiHeart, FiGithub, FiTwitter, FiMail, FiShield, FiFileText, FiInfo } = FiIcons;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-16">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="md:col-span-1">
            <div className="flex items-center space-x-2 mb-4">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <SafeIcon icon={FiActivity} className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                Web Down Status
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Privacy-focused website monitoring and status checking tool. 
              No data collection, no tracking, just instant results.
            </p>
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
              <SafeIcon icon={FiShield} className="h-4 w-4 text-green-500" />
              <span>Privacy First</span>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 uppercase tracking-wider">
              Product
            </h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/about" 
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  About Us
                </Link>
              </li>
              <li>
                <a 
                  href="https://github.com/yourusername/web-down-status" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  Source Code
                </a>
              </li>
              <li>
                <a 
                  href="#features" 
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  Features
                </a>
              </li>
              <li>
                <a 
                  href="#api" 
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  API Documentation
                </a>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 uppercase tracking-wider">
              Legal
            </h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/privacy" 
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link 
                  to="/terms" 
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link 
                  to="/disclaimer" 
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  Disclaimer
                </Link>
              </li>
              <li>
                <a 
                  href="mailto:contact@webdownstatus.com" 
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  Contact Us
                </a>
              </li>
            </ul>
          </div>

          {/* Connect Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 uppercase tracking-wider">
              Connect
            </h3>
            <div className="space-y-3">
              <div className="flex space-x-3">
                <a 
                  href="https://github.com/yourusername/web-down-status" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  aria-label="GitHub Repository"
                >
                  <SafeIcon icon={FiGithub} className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </a>
                <a 
                  href="https://twitter.com/webdownstatus" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  aria-label="Twitter"
                >
                  <SafeIcon icon={FiTwitter} className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </a>
                <a 
                  href="mailto:contact@webdownstatus.com"
                  className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  aria-label="Email Contact"
                >
                  <SafeIcon icon={FiMail} className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </a>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <SafeIcon icon={FiInfo} className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-blue-800 dark:text-blue-400 font-medium mb-1">
                      Open Source & Free
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      This tool is completely free and open source. No registration required.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 dark:border-gray-700 mt-8 pt-6">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
              <span>© {currentYear} Web Down Status.</span>
              <span>Made with</span>
              <SafeIcon icon={FiHeart} className="h-4 w-4 text-red-500" />
              <span>for the web community.</span>
            </div>
            
            <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center space-x-1">
                <SafeIcon icon={FiShield} className="h-3 w-3" />
                <span>No cookies</span>
              </span>
              <span className="flex items-center space-x-1">
                <SafeIcon icon={FiFileText} className="h-3 w-3" />
                <span>No tracking</span>
              </span>
              <span className="flex items-center space-x-1">
                <SafeIcon icon={FiActivity} className="h-3 w-3" />
                <span>Privacy first</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;